import { createContext, useCallback, useEffect, useState } from 'react';
import useCities from '../../../../Common/Filters/hooks/useCities';
import useCensusYears from '../../hooks/useCensusYears';
import { chooseProfileFilter } from '../../../../../../helpers/chooseProfileFilter';

export const CensusFiltersContext = createContext();

const emptyCity = {
    value: '',
    label: 'Selecione',
    governance_room_client_city_id: null,
    governance_room_geo_city_id: null
};

function readSavedCity() {
    try {
        const savedCity = localStorage.getItem('dashboard_city');
        return savedCity ? JSON.parse(savedCity) : null;
    } catch (error) {
        return null;
    }
}

const CensusFiltersProvider = ({ children }) => {
    // Usuário vinculado a um município só vê esse município: a cidade é fixa e o filtro fica oculto.
    const userClientCityId = chooseProfileFilter().governance_room_client_city_id || null;
    const isCityLocked = Boolean(userClientCityId);

    const { data: cities = [] } = useCities();
    const [selectedCity, setSelectedCity] = useState(() => (isCityLocked ? null : readSavedCity()));

    const userCity =
        cities.find((city) => city.governance_room_client_city_id === userClientCityId) || cities[0];

    const isSelectedCityAvailable = cities.some((city) => city.value === selectedCity?.value);

    const cidade = isCityLocked
        ? userCity || emptyCity
        : (isSelectedCityAvailable && selectedCity) || cities[0] || emptyCity;

    // Ano: o escolhido, se a cidade tiver dados dele; senão o mais recente importado.
    const { data: years = [], isFetched: areYearsFetched } = useCensusYears(cidade.value);
    const [selectedYear, setSelectedYear] = useState(null);

    // Enquanto os anos não chegam, o ano fica null e as consultas do Censo esperam: evita buscar
    // primeiro sem ano e de novo com o ano, o que fazia a tela piscar.
    const yearValue = years.includes(selectedYear?.value) ? selectedYear.value : years[0] || '';
    const year = !areYearsFetched
        ? { value: null, label: '…' }
        : yearValue
            ? { value: yearValue, label: yearValue }
            : { value: '', label: '—' };
    const yearOptions = years.map((value) => ({ value, label: value }));

    const onFilterChange = useCallback(
        (name, value) => {
            if (name === 'cidade' && !isCityLocked) {
                setSelectedCity(value);
            }
            if (name === 'year') {
                setSelectedYear(value);
            }
        },
        [isCityLocked]
    );

    useEffect(() => {
        if (!isCityLocked && cidade.value) {
            localStorage.setItem('dashboard_city', JSON.stringify(cidade));
        }
    }, [cidade, isCityLocked]);

    return (
        <CensusFiltersContext.Provider
            value={{
                filters: { cidade, year },
                onFilterChange,
                cities,
                yearOptions,
                hasCensusData: !areYearsFetched || years.length > 0,
                isCityLocked
            }}
        >
            {children}
        </CensusFiltersContext.Provider>
    );
};

export default CensusFiltersProvider;
